import './productPreviewGallery.scss';

import {
    ProductImage,
    ProductMedia,
    ProductVideo,
} from '../../../classes/ProductMedia';
import React, { useEffect, useMemo, useState } from 'react';

import FsLightbox from 'fslightbox-react';
import { GatsbyImage } from 'gatsby-plugin-image';

import ProductPreviewCarrousel from './ProductPreviewCarrousel';
import ProductPreviewThumbnails from './ProductPreviewThumbnails';

import { usePageLocalization } from '@sbi-international/boutik.gatsby.plugin.multilocale';
import { useTranslation } from 'react-i18next';
import useMobileDetect from '../../../hooks/useMobileDetect';
import { Icon } from '../../primitives';

type ProductPreviewGalleryProps = {
    product: Queries.ProductJsonTemplateFragment;
};

export default function ProductPreviewGallery(
    props: ProductPreviewGalleryProps
) {
    const { product } = props;
    const isAndroid = useMobileDetect().isAndroid();
    const { currentLocale } = usePageLocalization();

    const [selectedImage, setSelectedImage] = useState(0);
    const [isLightBoxVisible, setIsLightBoxVisible] = useState(false);
    const [toggler3D, setToggler3D] = useState(false);
    const [isStartAR, setIsStartAR] = useState(false);
    const [medias, setMedias] = useState<ProductMedia[]>([]);
    const showInMySpaceButton = !product?.slug?.['en_CA']?.includes('inserts');

    const { t } = useTranslation('templatePages');

    useEffect(() => {
        const medias: ProductMedia[] = [];

        if (product.fields?.images != null) {
            medias.push(
                ...product.fields?.images
                    .filter((i:any) => i != null)
                    .map(
                        (ii:any) =>
                            new ProductImage(
                                ii as Queries.ProductsJsonFieldsImages
                            )
                    )
            );
        }

        if (product.attributes?.video_ids?.values != null) {
            medias.push(
                ...product.attributes?.video_ids?.values
                    .filter((i: any) => i != null)
                    .map((ii: any) => new ProductVideo(ii![currentLocale] as string))
            );
        }

        const videoIds = medias
            .filter((i) => i instanceof ProductVideo)
            .map((video) => (video as ProductVideo).videoId) as string[];

        if (videoIds.length > 0) {
            fetch(
                `https://youtube.googleapis.com/youtube/v3/videos?part=snippet&id=${videoIds.join(
                    ','
                )}&key=${process.env.GATSBY_GOOGLE_API_KEY}`
            )
                .then((response) => response.json())
                .then((result) => {
                    populateYoutubeThumbnails(medias, result);
                })
                .finally(() => {
                    setMedias(medias);
                });
        } else {
            setMedias(medias);
        }
    }, [product.fields?.images]);

    const populateYoutubeThumbnails = (
        productMedias: ProductMedia[],
        result: any
    ): ProductMedia[] => {
        if (result['items']) {
            productMedias.forEach((media) => {
                if (
                    media instanceof ProductVideo &&
                    result.items.some((d: any) => d.id == media.videoId)
                ) {
                    const item = result.items.find(
                        (d: any) => d.id == media.videoId
                    );
                    media.thumbnailUrl =
                        item['snippet']['thumbnails']['high']['url'];
                }
            });
        }
        return productMedias;
    };

    const slidersReactNode = (thumbnailsImages: any[]): React.ReactNode[] => {
        const imagesNode: React.ReactNode[] = [];

        thumbnailsImages.forEach((media, forEachIndex) => {
            if (media instanceof ProductImage) {
                imagesNode.push(
                    <GatsbyImage
                        image={media.image?.childImageSharp?.gatsbyImageData!}
                        alt={`${
                            product.title?.[currentLocale]
                        } | thumbnail | Century Heating | ${
                            product.category?.includes('Wood Stoves')
                                ? t('stove')
                                : t('insert')
                        }`}
                    />
                );
            }
            if (media instanceof ProductVideo) {
                imagesNode.push(
                    <iframe
                        width='420'
                        height='315'
                        src={`https://www.youtube.com/embed/${media.videoId}`}
                    ></iframe>
                );
            }
        });
        return imagesNode;
    };

    const imagesSrc = useMemo(
        () =>
            medias.map((media) =>
                media instanceof ProductImage
                    ? ((media.image as Queries.ProductsJsonFieldsImages)
                          .childImageSharp?.gatsbyImageData.images?.fallback
                          ?.src as string)
                    : `https://www.youtube.com/watch?v=${
                          (media as ProductVideo).videoId as string
                      }`
            ),
        [medias]
    );

    const GetCurrentVideoThumbnail = (index: number): React.ReactElement => {
        return (
            <div>
                {' '}
                <img
                    src={(medias[index] as ProductVideo).thumbnailUrl}
                    alt={`${
                        product.title?.[currentLocale]
                    } | Century Heating | ${
                        product.category?.includes('Wood Stoves')
                            ? t('stove')
                            : t('insert')
                    }`}
                    className='image toggle video'
                    title=''
                />
                <Icon
                    d='M44.9,14.5c-0.4-2.2-2.3-3.8-4.5-4.3C37.1,9.5,31,9,24.4,9c-6.6,0-12.8,0.5-16.1,1.2c-2.2,0.5-4.1,2-4.5,4.3C3.4,17,3,20.5,3,25s0.4,8,0.9,10.5c0.4,2.2,2.3,3.8,4.5,4.3c3.5,0.7,9.5,1.2,16.1,1.2s12.6-0.5,16.1-1.2c2.2-0.5,4.1-2,4.5-4.3c0.4-2.5,0.9-6.1,1-10.5C45.9,20.5,45.4,17,44.9,14.5z M19,32V18l12.2,7L19,32z'
                    size='100'
                    fill='darkgrey'
                    viewBox='0 0 50 50'
                />
            </div>
        );
    };

    const GetCurrentImgThumbnail = (index: number): React.ReactElement => {
        const gatsbyImageData_ = (medias[index] as ProductImage).image
            ?.childImageSharp?.gatsbyImageData;
        return (
            <>
                {gatsbyImageData_ && (
                    <GatsbyImage
                        image={gatsbyImageData_}
                        alt={`${
                            product.title?.[currentLocale]
                        } | Century Heating | ${
                            product.category?.includes('Wood Stoves')
                                ? t('stove')
                                : t('insert')
                        } `}
                    />
                )}
            </>
        );
    };

    const GetCurrentMedia = (index: number): React.ReactElement => {
        switch (medias[selectedImage].constructor) {
            case ProductImage:
                return GetCurrentImgThumbnail(index);
            case ProductVideo:
                return GetCurrentVideoThumbnail(index);
            default:
                return <></>;
        }
    };

    return (
        <>
            <div className='product-gallery century-product-galery'>
                {medias.length > 1 && (
                    <div className='thumbnails century-product-thumbnails'>
                        <ProductPreviewThumbnails
                            product={product}
                            productMedias={medias}
                            selectedImage={selectedImage}
                            setSelectedImage={setSelectedImage}
                        />
                    </div>
                )}
                {medias && (
                    <div>
                        <div
                            className='featured-image'
                            onClick={() => {
                                medias?.[selectedImage] &&
                                    setIsLightBoxVisible(!isLightBoxVisible);
                            }}
                        >
                            {medias.length > 0 &&
                                GetCurrentMedia(selectedImage)}
                        </div>
                        <ProductPreviewCarrousel
                            carrouselMedias={slidersReactNode(medias)}
                        />
                    </div>
                )}
            </div>
            <FsLightbox
                toggler={isLightBoxVisible}
                sources={imagesSrc as string[]}
                sourceIndex={selectedImage}
                exitFullscreenOnClose={true}
            />
        </>
    );
}
